import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Mail } from 'lucide-react';

const API = '';

const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

/**
 * ContactAutocomplete — Chip-Input / Single-Input mit Vorschlägen aus /api/contacts/suggest.
 *
 * Props:
 *  - value: string (single-Mode) | string[] (multiple-Mode)
 *  - onChange: (val) => void
 *  - kundeId: optional, priorisiert Kontakte des Kunden im Suggest
 *  - placeholder: string
 *  - multiple: boolean — true = Chip-Mode (CC/BCC), false = Single-Input (An)
 *  - disabled: boolean
 *  - autoFocus: boolean
 *  - id: optional DOM-ID
 */
export default function ContactAutocomplete({
  value,
  onChange,
  kundeId,
  placeholder = '',
  multiple = false,
  disabled = false,
  autoFocus = false,
  id,
}) {
  const [query, setQuery] = useState(multiple ? '' : String(value || ''));
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  // Synchronize single-mode external value into input
  useEffect(() => {
    if (!multiple) setQuery(String(value || ''));
  }, [value, multiple]);

  const chips = multiple ? (Array.isArray(value) ? value : []) : [];

  const fetchSuggestions = useCallback(async (q) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (kundeId) params.set('kunde_id', String(kundeId));
      params.set('limit', '10');
      const res = await fetch(`${API}/api/contacts/suggest?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) {
        if (reqId === reqIdRef.current) setSuggestions([]);
        return;
      }
      const data = await res.json();
      if (reqId !== reqIdRef.current) return; // stale
      // Filter: chips bereits gewählt → ausblenden
      const taken = new Set((multiple ? chips : []).map(s => String(s || '').toLowerCase()));
      const list = (data.suggestions || []).filter(s => {
        const e = String(s.primary_email || '').toLowerCase();
        return e && !taken.has(e);
      });
      setSuggestions(list);
    } catch {
      if (reqId === reqIdRef.current) setSuggestions([]);
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [kundeId, multiple, chips]);

  // Debounced fetch on query change while open
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchSuggestions(query.trim());
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, open, kundeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Outside-click closes
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const addChip = (raw) => {
    const email = String(raw || '').trim();
    if (!email || !isValidEmail(email)) return false;
    const lower = email.toLowerCase();
    const exists = chips.some(c => String(c || '').toLowerCase() === lower);
    if (exists) return false;
    onChange([...chips, email]);
    return true;
  };

  const removeChip = (idx) => {
    const next = chips.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const pickSuggestion = (s) => {
    const mail = String(s.primary_email || '').trim();
    if (!mail) return;
    if (multiple) {
      if (addChip(mail)) {
        setQuery('');
        setActiveIdx(-1);
        setOpen(true);
      }
    } else {
      onChange(mail);
      setQuery(mail);
      setOpen(false);
    }
  };

  const commitFree = () => {
    if (!multiple) return;
    const tokens = query.split(/[,;\s]+/).map(t => t.trim()).filter(Boolean);
    let added = 0;
    for (const tok of tokens) if (addChip(tok)) added++;
    if (added > 0) { setQuery(''); setActiveIdx(-1); }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      if (open && activeIdx >= 0 && suggestions[activeIdx]) {
        e.preventDefault();
        pickSuggestion(suggestions[activeIdx]);
      } else if (multiple) {
        e.preventDefault();
        commitFree();
      }
    } else if ((e.key === ',' || e.key === ';') && multiple) {
      e.preventDefault();
      commitFree();
    } else if (e.key === 'Backspace' && multiple && !query && chips.length) {
      e.preventDefault();
      removeChip(chips.length - 1);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
    }
  };

  const onBlur = () => {
    // Slight delay so click on suggestion still registers
    setTimeout(() => {
      if (multiple && query.trim()) commitFree();
      if (!multiple) onChange(query.trim());
    }, 120);
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className={`w-full bg-surface-inset border border-line rounded-lg px-2 py-1.5 text-sm text-content focus-within:border-primary-500/50 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
        {multiple && chips.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1">
            {chips.map((c, i) => {
              const ok = isValidEmail(c);
              return (
                <span key={`${c}-${i}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] border ${ok ? 'bg-primary-500/10 border-primary-500/30 text-content' : 'bg-red-50 border-red-200 text-red-600'}`}>
                  <Mail className="w-3 h-3" />
                  <span className="truncate max-w-[220px]">{c}</span>
                  <button
                    type="button"
                    onClick={() => removeChip(i)}
                    className="hover:text-red-500"
                    aria-label="Remove"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); setActiveIdx(-1); }}
          onFocus={() => setOpen(true)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          autoFocus={autoFocus}
          disabled={disabled}
          className="w-full bg-transparent outline-none text-sm text-content placeholder:text-content-tertiary px-1"
        />
      </div>

      {open && (suggestions.length > 0 || loading) && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-surface-card border border-line rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {loading && (
            <div className="px-3 py-2 text-[12px] text-content-tertiary">…</div>
          )}
          {!loading && suggestions.map((s, i) => {
            const active = i === activeIdx;
            return (
              <button
                key={s.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickSuggestion(s)}
                onMouseEnter={() => setActiveIdx(i)}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 ${active ? 'bg-primary-500/10' : 'hover:bg-surface-inset'}`}
              >
                <div className="w-7 h-7 rounded-full bg-primary-500/10 text-[#338edc] flex items-center justify-center text-[11px] font-semibold flex-shrink-0">
                  {(s.anzeige_name || s.primary_email || '?').slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-content truncate">{s.anzeige_name || s.primary_email}</span>
                    {s.kunde_firma && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary-500/10 text-[#338edc] truncate max-w-[140px]">{s.kunde_firma}</span>
                    )}
                    {s.status === 'vorgeschlagen' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-700">●</span>
                    )}
                  </div>
                  <div className="text-[11px] text-content-tertiary truncate">{s.primary_email}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
